import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useChatStore, Message } from '@/store/chatStore';
import { addSentryBreadcrumb, setSentryUser } from '@/utils/sentryConfig';
import { getTokenUserFromCookieChatbot } from '@/utils/chatbotGetToken';
import { getFeatureFlagsChatbot } from '@/utils/getFeatureFlags';
import { openCallChatbot } from '@/utils/openCall';
import { getCourseNameFromUrl } from '@/utils/urlUtils';
import {
  CHATBOT_CHAT_MSJ_FROM_BOT,
  CHATBOT_CHAT_MSJ_FROM_CLIENT,
  CHATBOT_CHUNK_EVENT,
  CHATBOT_CONVERSATION_HISTORY,
  CHATBOT_ERROR_EVENT,
  CHATBOT_HITL_INTERRUPT_EVENT,
  CHATBOT_HITL_DECISION_EVENT,
  CHATBOT_HITL_RESUME_EVENT,
  CHATBOT_MODE_CHATBOT,
  CHATBOT_MODE_RECOMMENDER,
  CHATBOT_OUTSIDE_AUDIO,
  CHATBOT_OUTSIDE_MESSAGE,
  CHATBOT_RAW_USER,
  CHATBOT_RESET_EVENT,
  CHATBOT_SOCKET_CONNECT,
  CHATBOT_SOCKET_DISCONNECT,
  CHATBOT_SOCKET_MESSAGE,
  CHATBOT_STREAM_END_EVENT,
  CHATBOT_STREAM_ERROR_EVENT,
  CHATBOT_TOKEN_EVENT,
  CHATBOT_TOKEN_STRING,
  CHATBOT_TOOL_EVENT,
  CHATBOT_WHATSAPP_MESSAGE,
} from '@/utils/constants';
import {
  generateUuidChatbot,
  getCurrentSessionIndexChatbot,
  updateSessionIndexChatbot,
} from '@/utils/uuid';

const verboseChatbot = import.meta.env.VITE_APP_VERBOSE === 'true';
const urlReference = import.meta.env.VITE_APP_CUSTOM_URL || window.location.href;
const urlContainsPanel = urlReference.includes('/panel/');

interface UseSocketOptions {
  uuid: string;
  onUuidChange: (newUuid: string) => void;
}

interface UseSocketReturn {
  socket: Socket | null;
  sendMessage: (params: SendMessageParams) => void;
  sendHitlDecision: (decision: 'approve' | 'reject', threadId: string, message?: string) => void;
  resetChat: (e: React.MouseEvent) => void;
  connect: () => void;
  disconnect: () => void;
}

export interface SendMessageParams {
  from: string;
  message: string;
  audio?: string | null;
  isAudio?: boolean;
  typeMode?: string;
}

// Buffer type for streaming chunks accumulation
interface ChunkBufferData {
  content: string;
  channel: string;
}

export function useSocket({ uuid, onUuidChange }: UseSocketOptions): UseSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const uuidRef = useRef(uuid);

  // Streaming performance optimization: Buffer to accumulate chunks before updating store
  // This reduces the number of store updates by batching multiple chunks together
  const chunkBufferRef = useRef<Map<string, ChunkBufferData>>(new Map());
  const flushScheduledRef = useRef<boolean>(false);

  // Update ref when uuid changes
  useEffect(() => {
    uuidRef.current = uuid;
  }, [uuid]);

  // Store selectors
  const {
    isOpen,
    isFirstRender,
    openCount,
    isConnected,
    connectionFailed,
    isResetting,
    lastResetTimestamp,
    isNewSessionAfterAuthChange,
    skipMaintenanceMode,
    token,
    userData,
    portalId,
    currentMode,
    setIsConnected,
    setConnectionFailed,
    setConfigFeatureFlag,
    setSocketToken,
    setIsLoading,
    setHasConversationHistory,
    setIsLoadingHistory,
    setIsNewSessionAfterAuthChange,
    setIsResetting,
    setLastResetTimestamp,
    addMessage,
    updateMessage,
    clearMessages,
    setAllMessages,
    incrementUnreadCount,
    // Optimized streaming actions (new)
    processStreamingChunk,
    finalizeStreamingMessage,
    // Legacy streaming actions (deprecated - kept for backwards compatibility)
    initStreamingMessage,
    appendToStreamingMessage,
    completeStreamingMessage,
    removeStreamingMessage,
    addActiveTool,
    clearActiveTools,
    resetChat: resetChatStore,
  } = useChatStore();

  // Helper to add message with duplicate check
  const addMessageToList = useCallback((
    { from, message, typeMode, audio = null, createdAt = null }: {
      from: string;
      message: string | null;
      typeMode?: string;
      audio?: string | null;
      createdAt?: string | null;
    },
    id: string | number | null = null
  ) => {
    const mode = typeMode || currentMode();

    let finalMessage = message;
    if (from === CHATBOT_CHAT_MSJ_FROM_BOT && skipMaintenanceMode && message) {
      finalMessage = `${message}\n\n**[MAINTENANCE]**`;
    }

    const newMessage: Message = {
      id: id || -Date.now(),
      from,
      message: finalMessage,
      audio,
      score: 0,
      createdAt: createdAt || new Date().toISOString(),
    };

    addMessage(mode as typeof CHATBOT_MODE_CHATBOT | typeof CHATBOT_MODE_RECOMMENDER, newMessage);

    if (from === CHATBOT_CHAT_MSJ_FROM_BOT && !isOpen) {
      incrementUnreadCount();
    }
  }, [addMessage, currentMode, incrementUnreadCount, isOpen, skipMaintenanceMode]);

  // Helper to add audio message
  const addAudioToList = useCallback((
    { from, audio, typeMode, createdAt = null }: {
      from: string;
      audio: string;
      typeMode?: string;
      createdAt?: string | null;
    },
    id: string | number | null = null
  ) => {
    const mode = typeMode || currentMode();

    const newMessage: Message = {
      id: id || -Date.now(),
      from,
      message: null,
      audio,
      score: 0,
      createdAt: createdAt || new Date().toISOString(),
    };

    addMessage(mode as typeof CHATBOT_MODE_CHATBOT | typeof CHATBOT_MODE_RECOMMENDER, newMessage);
  }, [addMessage, currentMode]);

  // Update streaming message in list
  /** @deprecated Use flushChunkBuffer with processStreamingChunk instead. Kept for backwards compatibility. */
  const updateStreamingMessageInList = useCallback((
    messageId: string | number,
    accumulatedContent: string,
    channel: string,
    createdAt: string
  ) => {
    let finalMessage = accumulatedContent;
    if (skipMaintenanceMode && accumulatedContent) {
      finalMessage = `${accumulatedContent}\n\n**[MAINTENANCE]**`;
    }

    const mode = channel as typeof CHATBOT_MODE_CHATBOT | typeof CHATBOT_MODE_RECOMMENDER;
    const messages = useChatStore.getState().messages[mode];
    const existingIndex = messages.findIndex(msg => msg.id === messageId);

    if (existingIndex >= 0) {
      updateMessage(mode, messageId, {
        message: finalMessage,
        isStreaming: true,
      });
    } else {
      const newMessage: Message = {
        id: messageId,
        from: CHATBOT_CHAT_MSJ_FROM_BOT,
        message: finalMessage,
        audio: null,
        score: 0,
        createdAt,
        isStreaming: true,
      };
      addMessage(mode, newMessage);

      if (!isOpen) {
        incrementUnreadCount();
      }
    }
  }, [addMessage, incrementUnreadCount, isOpen, skipMaintenanceMode, updateMessage]);

  /**
   * Flushes the accumulated chunk buffer to the store.
   * Called on each animation frame to batch multiple chunks into a single store update.
   * This significantly reduces re-renders during streaming.
   */
  const flushChunkBuffer = useCallback(() => {
    const buffer = chunkBufferRef.current;
    if (buffer.size === 0) {
      flushScheduledRef.current = false;
      return;
    }

    const state = useChatStore.getState();

    buffer.forEach((data, id) => {
      // Use the optimized processStreamingChunk action that combines
      // initStreamingMessage + appendToStreamingMessage + updateMessage in one atomic operation
      processStreamingChunk(id, data.content, data.channel, state.skipMaintenanceMode);

      // Increment unread count if chat is closed (matching legacy behavior)
      if (!state.isOpen) {
        incrementUnreadCount();
      }
    });

    buffer.clear();
    flushScheduledRef.current = false;
  }, [processStreamingChunk, incrementUnreadCount]);

  // Create socket connection
  const createSocket = useCallback(() => {
    const sessionToken = window.sessionStorage.getItem(CHATBOT_TOKEN_STRING);
    const config = {
      query: {
        token: isFirstRender ? null : sessionToken,
        uuid: uuidRef.current,
      },
      transports: ['websocket'] as ('websocket')[],
      autoConnect: false,
    };
    return io(import.meta.env.VITE_APP_SOCKET_URL, config);
  }, [isFirstRender]);

  // Setup socket listeners
  const setupSocketListeners = useCallback((socket: Socket) => {
    socket.on(CHATBOT_SOCKET_CONNECT, () => {
      setConnectionFailed(false);
      const queryToken = socket.io.opts.query?.token as string | undefined;

      if (queryToken) {
        getFeatureFlagsChatbot(queryToken, (response) => {
          if (response) {
            setConfigFeatureFlag(response);
          }
          setIsConnected(true);
        });
      } else {
        setIsConnected(true);
      }
    });

    socket.on(CHATBOT_OUTSIDE_MESSAGE, ({ message, id, createdAt, timestamp }, channel) => {
      if (!message) {
        console.error('The message is empty');
        return;
      }
      const normalizedCreatedAt = timestamp || createdAt;
      addMessageToList({ from: CHATBOT_CHAT_MSJ_FROM_BOT, typeMode: channel, message, createdAt: normalizedCreatedAt }, id);
    });

    socket.on(CHATBOT_OUTSIDE_AUDIO, ({ audio, id, createdAt, timestamp }, channel) => {
      setIsLoading(channel as typeof CHATBOT_MODE_CHATBOT, false);
      const normalizedCreatedAt = timestamp || createdAt;
      addAudioToList({ from: CHATBOT_CHAT_MSJ_FROM_BOT, typeMode: channel, audio, createdAt: normalizedCreatedAt }, id);
    });

    socket.on(CHATBOT_ERROR_EVENT, ({ message }) => {
      console.error('Error received: ', message);
    });

    socket.on(CHATBOT_TOKEN_EVENT, ({ accessToken }) => {
      setSocketToken(accessToken);
      window.sessionStorage.setItem(CHATBOT_TOKEN_STRING, accessToken);
      socket.io.opts.query = { token: accessToken, uuid: uuidRef.current };
      socket.disconnect().connect();
    });

    socket.on(CHATBOT_SOCKET_MESSAGE, ({ message, id, createdAt, timestamp }, channel) => {
      if (!message) {
        console.error('The message is empty');
        return;
      }
      setIsLoading(channel as typeof CHATBOT_MODE_CHATBOT, false);
      const normalizedCreatedAt = timestamp || createdAt;
      addMessageToList({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message, typeMode: channel, createdAt: normalizedCreatedAt }, id);
    });

    // Handle streaming chunks (Optimized with requestAnimationFrame throttling)
    // This accumulates chunks in a buffer and flushes them on each animation frame
    // to reduce store updates and re-renders during rapid streaming
    socket.on(CHATBOT_CHUNK_EVENT, ({ message, id }, channel) => {
      const effectiveChannel = channel || currentMode();

      if (!message || message.length === 0) {
        return;
      }

      // Accumulate chunk in buffer
      const existing = chunkBufferRef.current.get(id);
      chunkBufferRef.current.set(id, {
        content: (existing?.content || '') + message,
        channel: effectiveChannel,
      });

      // Schedule flush on next animation frame if not already scheduled
      // requestAnimationFrame provides natural throttling at ~60fps (~16ms intervals)
      // This means multiple chunks received within 16ms will be batched into a single update
      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        requestAnimationFrame(flushChunkBuffer);
      }
    });

    // Handle tool calls
    socket.on(CHATBOT_TOOL_EVENT, ({ message: toolName, id }, channel) => {
      addActiveTool(id, toolName);
    });

    // Handle stream end (Optimized)
    // Flushes any pending buffered content before finalizing the message
    socket.on(CHATBOT_STREAM_END_EVENT, ({ id }, channel) => {
      const effectiveChannel = channel || currentMode();

      // First, flush any pending content from the buffer for this message
      // This ensures all accumulated chunks are processed before marking as complete
      if (chunkBufferRef.current.has(id)) {
        const data = chunkBufferRef.current.get(id)!;
        const state = useChatStore.getState();
        processStreamingChunk(id, data.content, data.channel, state.skipMaintenanceMode);
        chunkBufferRef.current.delete(id);
      }

      // Use the optimized finalizeStreamingMessage that combines
      // completeStreamingMessage + clearActiveTools + setIsLoading in one atomic operation
      finalizeStreamingMessage(id);
    });

    // Handle stream error
    // Also clears any buffered content for this message
    socket.on(CHATBOT_STREAM_ERROR_EVENT, ({ message, id }, channel) => {
      console.error('❌ [STREAM_ERROR]', message || 'Unknown error');

      const effectiveChannel = channel || currentMode();

      // Clear any buffered content for this message
      if (chunkBufferRef.current.has(id)) {
        chunkBufferRef.current.delete(id);
      }

      removeStreamingMessage(id);
      clearActiveTools(id);
      setIsLoading(effectiveChannel as typeof CHATBOT_MODE_CHATBOT, false);

      addMessageToList({
        from: CHATBOT_CHAT_MSJ_FROM_BOT,
        typeMode: effectiveChannel,
        message: message || 'Ocurrió un error. Por favor, intenta de nuevo.',
        createdAt: new Date().toISOString(),
      }, id);
    });

    // Handle HITL (Human-in-the-Loop) interrupt
    // This is emitted when a tool requires user approval before execution
    socket.on(CHATBOT_HITL_INTERRUPT_EVENT, (data, channel) => {
      if (verboseChatbot) {
        console.log('🛑 [HITL_INTERRUPT] Tool requires approval:', data);
      }

      const { setHitlInterrupt, setIsLoading } = useChatStore.getState();
      const effectiveChannel = channel || currentMode();

      // Stop loading indicator
      setIsLoading(effectiveChannel as typeof CHATBOT_MODE_CHATBOT, false);

      // Store the interrupt data for UI to display approval dialog
      setHitlInterrupt({
        id: data.id,
        toolName: data.toolName,
        toolArgs: data.toolArgs || {},
        toolCallId: data.toolCallId,
        description: data.description,
        threadId: data.threadId,
        courseInfo: data.courseInfo, // Include course information for enrollment approvals
        timestamp: new Date().toISOString(),
      });
    });

    // Handle HITL resume confirmation
    // This is emitted when the server starts resuming execution after user decision
    socket.on(CHATBOT_HITL_RESUME_EVENT, (data, channel) => {
      if (verboseChatbot) {
        console.log('▶️ [HITL_RESUME] Execution resuming:', data);
      }

      const { clearHitlInterrupt, setIsLoading } = useChatStore.getState();
      const effectiveChannel = channel || currentMode();

      // Clear the interrupt state
      clearHitlInterrupt();

      // Show loading while the tool executes
      setIsLoading(effectiveChannel as typeof CHATBOT_MODE_CHATBOT, true);
    });

    // Handle conversation history
    socket.on(CHATBOT_CONVERSATION_HISTORY, (conversationHistory) => {
      const historyMode = import.meta.env.VITE_CONVERSATION_HISTORY_MODE || 'enabled';

      if (historyMode === 'disabled') {
        if (verboseChatbot) {
          console.log('🚫 Conversation history disabled by environment variable');
        }
        setIsLoadingHistory(false);
        return;
      }

      const state = useChatStore.getState();
      if (state.isResetting || (Date.now() - state.lastResetTimestamp) < 1000) {
        if (verboseChatbot) {
          console.log('🚫 BLOCKING history load - just after reset');
        }
        setIsLoadingHistory(false);
        return;
      }

      setIsLoadingHistory(true);

      if (state.isNewSessionAfterAuthChange) {
        if (verboseChatbot) {
          console.log('🚫 BLOCKING history load - new session after auth change');
        }
        setIsLoadingHistory(false);
        setIsNewSessionAfterAuthChange(false);
        clearMessages(CHATBOT_MODE_CHATBOT);
        clearMessages(CHATBOT_MODE_RECOMMENDER);
        setHasConversationHistory(false);
        return;
      }

      const hasValidHistory =
        conversationHistory &&
        conversationHistory.messages &&
        Array.isArray(conversationHistory.messages) &&
        conversationHistory.messages.length > 0;

      if (!hasValidHistory) {
        if (verboseChatbot) {
          console.log('📭 No conversation history found or empty history');
        }
        setIsLoadingHistory(false);
        return;
      }

      setHasConversationHistory(true);
      const channel = currentMode();

      const sortedMessages = [...conversationHistory.messages.filter((msg: { userHistory?: boolean }) => msg.userHistory)].sort((a, b) => {
        const dateA = (a.timestamp || a.createdAt) ? new Date(a.timestamp || a.createdAt).getTime() : 0;
        const dateB = (b.timestamp || b.createdAt) ? new Date(b.timestamp || b.createdAt).getTime() : 0;
        return dateA - dateB;
      });

      const newMessages: Message[] = sortedMessages.map((msg) => ({
        id: msg.id,
        from: msg.from === CHATBOT_RAW_USER ? CHATBOT_CHAT_MSJ_FROM_CLIENT : msg.from,
        message: msg.content,
        audio: msg.audio || null,
        score: 0,
        createdAt: msg.timestamp || msg.createdAt || new Date().toISOString(),
      }));

      setAllMessages(channel as typeof CHATBOT_MODE_CHATBOT, newMessages);

      if (verboseChatbot) {
        console.log('✅ Conversation history loaded successfully');
      }

      setIsLoadingHistory(false);
    });

    socket.on(CHATBOT_SOCKET_DISCONNECT, () => {
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Error connecting to socket: ', error);
      setConnectionFailed(true);
      if (import.meta.env.PROD) {
        addSentryBreadcrumb('Socket connection error', 'socket', 'error', {
          error: error.message,
        });
      }
      addMessageToList({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE });
    });

    socket.on('force-disconnect', () => {
      if (verboseChatbot) {
        console.log('🔄 Force Disconnect Socket Event: Minimizing chat dropdown');
      }

      if (socket.connected) {
        socket.disconnect();
      }

      const state = useChatStore.getState();
      if (!state.isResetting) {
        if (state.isOpen) {
          useChatStore.getState().setIsOpen(false);
        }
        if (state.isDropdownExpanded) {
          useChatStore.getState().setIsDropdownExpanded(false);
        }
      }
    });

    socket.on(CHATBOT_RESET_EVENT, (resetData) => {
      if (verboseChatbot) {
        console.log('🔄 Reset event received from server:', resetData);
      }

      if (resetData && resetData.newUuid && resetData.baseUuid) {
        const currentIndex = getCurrentSessionIndexChatbot();
        const newBaseUuid = resetData.baseUuid;
        const currentBaseUuid = uuidRef.current.replace(/ - \d+$/, '');

        if (newBaseUuid !== currentBaseUuid) {
          const newUuid = `${newBaseUuid} - ${currentIndex}`;
          updateSessionIndexChatbot(currentIndex);
          onUuidChange(newUuid);

          if (socket.connected) {
            socket.io.opts.query = { ...socket.io.opts.query, uuid: newUuid };
          }

          if (verboseChatbot) {
            console.log('🔄 UUID Updated from server reset event');
          }
        }
      }
    });
  }, [
    addAudioToList,
    addMessageToList,
    addActiveTool,
    appendToStreamingMessage,
    clearActiveTools,
    clearMessages,
    completeStreamingMessage,
    currentMode,
    finalizeStreamingMessage,
    flushChunkBuffer,
    initStreamingMessage,
    onUuidChange,
    processStreamingChunk,
    removeStreamingMessage,
    setAllMessages,
    setConfigFeatureFlag,
    setConnectionFailed,
    setHasConversationHistory,
    setIsConnected,
    setIsLoading,
    setIsLoadingHistory,
    setIsNewSessionAfterAuthChange,
    setSocketToken,
    updateStreamingMessageInList,
  ]);

  // Connect to socket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    const socket = createSocket();
    setupSocketListeners(socket);
    socket.connect();
    socketRef.current = socket;
  }, [createSocket, setupSocketListeners]);

  // Disconnect from socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  // Send message
  const sendMessage = useCallback(({ from, message, audio = null, isAudio = false, typeMode }: SendMessageParams) => {
    const tokenUser = getTokenUserFromCookieChatbot();
    const mode = typeMode || currentMode();
    const state = useChatStore.getState();

    if (tokenUser) {
      useChatStore.getState().setToken(tokenUser);
      if (import.meta.env.PROD) {
        setSentryUser(tokenUser, { authenticated: true });
      }
    }

    if (from === CHATBOT_CHAT_MSJ_FROM_CLIENT) {
      const recommenderIsOffline = mode === CHATBOT_MODE_RECOMMENDER && !state.recommenderStatusIsOnline;
      const chatbotIsOffline = mode === CHATBOT_MODE_CHATBOT && !state.chatbotStatusIsOnline;

      if (state.connectionFailed && !state.isConnected) {
        if (socketRef.current?.disconnected) {
          socketRef.current.connect();
        }
      }

      if (!state.isConnected || chatbotIsOffline || recommenderIsOffline) {
        addMessageToList({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE, typeMode: mode, audio });
        return;
      }

      const messages = state.messages[mode as typeof CHATBOT_MODE_CHATBOT];
      if (messages.length === 1 && socketRef.current) {
        // First message - send welcome first
        socketRef.current.emit(CHATBOT_SOCKET_MESSAGE, {
          from: CHATBOT_CHAT_MSJ_FROM_BOT,
          message: messages[0].message,
          url: urlReference,
          mode,
          base64: null,
          isAudio: false,
          tokenUser: state.token,
          userPanel: state.token && urlContainsPanel,
          portal: state.portalId,
          userData: state.userData,
        });

        addMessageToList({ from, message, audio });
        setIsLoading(mode as typeof CHATBOT_MODE_CHATBOT, true);

        // Track first message sent event
        try {
          const userId = state.userData?.id || tokenUser || 'anonymous';
          if ((window as any).dataLayer) {
            const courseName = getCourseNameFromUrl();
            (window as any).dataLayer.push({
              event: 'first_message_sent',
              chatbot_user_id: userId,
              chatbot_id: uuid,
              message_content: message.substring(0, 50),
              item_name: courseName,
            });
          }
        } catch (error) {
          console.error('Error tracking first message event:', error);
        }

        setTimeout(() => {
          socketRef.current?.emit(CHATBOT_SOCKET_MESSAGE, {
            from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
            message,
            url: urlReference,
            mode,
            base64: audio,
            isAudio,
            tokenUser: state.token,
            userPanel: state.token && urlContainsPanel,
            portal: state.portalId,
            userData: state.userData,
          });
        }, 2000);
        return;
      }

      socketRef.current?.emit(CHATBOT_SOCKET_MESSAGE, {
        from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
        message,
        url: urlReference,
        mode,
        base64: audio,
        isAudio,
        tokenUser: state.token,
        userPanel: state.token && urlContainsPanel,
        portal: state.portalId,
        userData: state.userData,
      });
      addMessageToList({ from, message, audio });
      setIsLoading(mode as typeof CHATBOT_MODE_CHATBOT, true);
    } else {
      addMessageToList({ from, message, audio });
    }
  }, [addMessageToList, currentMode, setIsLoading]);

  // Reset chat
  const resetChat = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    const state = useChatStore.getState();
    if (state.isResetting) {
      if (verboseChatbot) {
        console.log('🔄 Reset already in progress, ignoring duplicate request');
      }
      return;
    }

    setIsResetting(true);

    const currentIndex = getCurrentSessionIndexChatbot();
    const newIndex = parseInt(currentIndex) + 1;
    const oldUuid = uuidRef.current;
    const newUuid = generateUuidChatbot(uuidRef.current, newIndex);

    updateSessionIndexChatbot(newIndex.toString());
    onUuidChange(newUuid);

    if (verboseChatbot) {
      console.log('🔢 Session Index Increment:', {
        oldIndex: currentIndex,
        newIndex,
        oldUuid,
        newUuid,
      });
    }

    // Send reset event to server
    if (socketRef.current?.connected) {
      socketRef.current.emit(CHATBOT_RESET_EVENT, {
        oldUuid,
        newUuid,
        baseUuid: newUuid.replace(/ - \d+$/, ''),
        url: urlReference,
        tokenUser: state.token,
        userPanel: state.token && urlContainsPanel,
        portal: state.portalId,
        userData: state.userData,
      });
    }

    if (socketRef.current?.io?.opts) {
      socketRef.current.io.opts.query = { token: null, uuid: newUuid };
    }
    window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);

    // Reset store
    resetChatStore();
    setLastResetTimestamp(Date.now());

    // Reconnect
    if (socketRef.current) {
      if (socketRef.current.connected) {
        socketRef.current.disconnect();
      }

      setTimeout(() => {
        socketRef.current?.connect();
        openCallChatbot(newUuid);
      }, 100);
    }

    setTimeout(() => {
      setIsResetting(false);
    }, 500);
  }, [onUuidChange, resetChatStore, setIsResetting, setLastResetTimestamp]);

  /**
   * Sends a HITL decision to the server
   * @param decision - 'approve' or 'reject'
   * @param threadId - The thread ID where the interrupt occurred
   * @param message - Optional message for reject decisions
   */
  const sendHitlDecision = useCallback((
    decision: 'approve' | 'reject',
    threadId: string,
    message?: string
  ) => {
    if (!socketRef.current?.connected) {
      console.error('❌ [HITL] Cannot send decision: Socket not connected');
      return;
    }

    if (verboseChatbot) {
      console.log('📤 [HITL_DECISION] Sending decision:', { decision, threadId, message });
    }

    socketRef.current.emit(CHATBOT_HITL_DECISION_EVENT, {
      decision,
      threadId,
      message,
    });

    // Set loading state while waiting for response
    const effectiveChannel = currentMode();
    setIsLoading(effectiveChannel as typeof CHATBOT_MODE_CHATBOT, true);
  }, [currentMode, setIsLoading]);

  // Auto-connect when chat opens
  useEffect(() => {
    if (isOpen && !socketRef.current?.connected) {
      if (verboseChatbot) {
        console.log('🔌 Connecting socket (chat opened)...');
      }
      connect();
    }
  }, [isOpen, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending chunks in the buffer to prevent memory leaks
      chunkBufferRef.current.clear();
      flushScheduledRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  return {
    socket: socketRef.current,
    sendMessage,
    sendHitlDecision,
    resetChat,
    connect,
    disconnect,
  };
}

